import { useFormik } from 'formik';
import Select from 'react-select';
import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import SubmitButton from '../common/SubmitButton.jsx';
import { createManualLicenseFormFields } from '../../config/form-fields.js';
import ErrorMessage from '../common/ErrorMessage.jsx';
import SuccessMessage from '../common/SuccessMessage.jsx';
import LoadingSpinner from '../common/LoadingSpinner.jsx';
import useManualTsPlusLicenses from '../../hooks/useManualTsPlusLicenses.js';
import createManualLicenseValidation from '../../validation/create-manual-ts-plus-license-validations.js';
import useOffices from '../../hooks/useOffices.js';

const formatYMD = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '';
    return dt.toISOString().substring(0, 10); // yyyy-mm-dd
};

const badge = (status) => {
    const map = {
        ACTIVE: { bg: '#22c55e', fg: '#fff', label: 'Active' },
        AVAILABLE: { bg: '#eab308', fg: '#000', label: 'Available' },
        WAITING: { bg: '#3b82f6', fg: '#fff', label: 'Waiting' },
        ERROR: { bg: '#ef4444', fg: '#fff', label: 'Error' }
    };
    const s = map[status] || { bg: '#9ca3af', fg: '#fff' };
    return (
        <span
            style={{
                background: s.bg,
                color: s.fg,
                padding: '2px 8px',
                borderRadius: 9999,
                fontSize: 12,
                fontWeight: 700,
                marginLeft: 8
            }}>
            {status}
        </span>
    );
};

/**
 * CreateUpdateManualTsPlusLicenseForm component for rendering the form to create a new manual license.
 *
 * @component
 * @returns {JSX.Element} - The rendered CreateUpdateManualTsPlusLicenseForm component.
 */
const CreateUpdateManualTsPlusLicenseForm = ({ objectToUpdate }) => {
    const {
        error,
        loading,
        createManualLicense,
        updateManualLicense,
        manualLicense
    } = useManualTsPlusLicenses();

    const {
        getAllOffices,
        loading: officesLoading,
        error: officesError
    } = useOffices();

    const [officeOptions, setOfficeOptions] = useState([]);

    useEffect(() => {
        (async () => {
            const list = await getAllOffices();
            const opts = (list || []).map((o) => ({
                value: o._id || o.id,
                label: o.officeSysName || o.officeName || o.name || ''
            }));
            setOfficeOptions(opts);
        })();
    }, []);

    const sanitizeManualLicense = (lic) => ({
        license: lic.license || '',
        officeId: lic.officeId || null,
        officeName: lic.officeName || '',
        numOfUsers: lic.numOfUsers ?? null,
        createDate: lic.createDate || '',
        errorFlag: !!lic.errorFlag
    });

    const initialValues = objectToUpdate
        ? sanitizeManualLicense(objectToUpdate)
        : {
              ...createManualLicenseFormFields,
              officeId: null,
              officeName: '',
              createDate: '',
              errorFlag: false
          };

    const toNullIfEmpty = (v) => (v === '' || v === undefined ? null : v);

    const formik = useFormik({
        initialValues,
        validationSchema: createManualLicenseValidation,
        onSubmit: async (values) => {
            const payload = {
                ...values,
                officeId: toNullIfEmpty(values.officeId),
                officeName: toNullIfEmpty(values.officeName),
                errorFlag: !!values.errorFlag,
                createDate:
                    values.createDate && values.createDate !== ''
                        ? values.createDate
                        : new Date().toISOString()
            };

            // Prefer officeId; drop free-typed officeName to avoid drift
            if (payload.officeId) delete payload.officeName;

            // Clean server-unwanted props if present
            delete payload._id;
            delete payload.id;
            delete payload.createdAt;
            delete payload.updatedAt;
            delete payload.isManual;
            delete payload.status;

            if (objectToUpdate) {
                await updateManualLicense(objectToUpdate._id, payload);
            } else {
                await createManualLicense(payload);
            }
        }
    });

    const selectedOffice = useMemo(
        () =>
            officeOptions.find((o) => o.value === formik.values.officeId) ||
            null,
        [officeOptions, formik.values.officeId]
    );

    return (
        <>
            <form onSubmit={formik.handleSubmit}>
                <div className="newsc-form-container">
                    <label htmlFor="license" className="form-label">
                        License{' '}
                        {objectToUpdate?.status && badge(objectToUpdate.status)}
                    </label>
                    <input
                        id="license"
                        name="license"
                        type="text"
                        className="newsc-input"
                        placeholder="e.g. XXXX-XXXX-XXXX-XXXX"
                        {...formik.getFieldProps('license')}
                    />
                    {formik.touched.license && formik.errors.license && (
                        <ErrorMessage message={formik.errors.license} />
                    )}

                    {/* Office selection (recommended) */}
                    <label className="form-label">Office</label>
                    <Select
                        options={officeOptions}
                        value={selectedOffice}
                        onChange={(opt) =>
                            formik.setFieldValue('officeId', opt?.value || null)
                        }
                        placeholder="Select an office"
                    />
                    {officesLoading && <LoadingSpinner />}
                    {officesError && (
                        <ErrorMessage message={officesError.message} />
                    )}

                    {/* Manual office name fallback */}
                    <div
                        style={{
                            fontSize: 12,
                            opacity: 0.75
                        }}>
                        <p
                            style={{
                                fontSize: 12
                            }}>
                            Or type a manual office name if it’s not listed:
                        </p>
                    </div>
                    <input
                        id="officeName"
                        name="officeName"
                        type="text"
                        className="newsc-input"
                        placeholder="Manual Office Name (if not listed)"
                        value={formik.values.officeName || ''}
                        onChange={formik.handleChange}
                    />
                    {/* Surface either error (Joi XOR) */}
                    {(formik.errors.officeId || formik.errors.officeName) && (
                        <ErrorMessage
                            message={
                                formik.errors.officeId ||
                                formik.errors.officeName
                            }
                        />
                    )}

                    <label htmlFor="numOfUsers" className="form-label">
                        # of Users
                    </label>
                    <input
                        id="numOfUsers"
                        name="numOfUsers"
                        type="number"
                        className="newsc-input"
                        {...formik.getFieldProps('numOfUsers')}
                    />
                    {formik.touched.numOfUsers && formik.errors.numOfUsers && (
                        <ErrorMessage message={formik.errors.numOfUsers} />
                    )}

                    <label htmlFor="createDate" className="form-label">
                        Create Date
                    </label>
                    <input
                        id="createDate"
                        name="createDate"
                        type="date"
                        className="newsc-input"
                        value={formatYMD(formik.values.createDate)}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                    />
                    {formik.touched.createDate && formik.errors.createDate ? (
                        <ErrorMessage message={formik.errors.createDate} />
                    ) : null}
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            marginTop: 12
                        }}>
                        <input
                            id="errorFlag"
                            name="errorFlag"
                            type="checkbox"
                            checked={!!formik.values.errorFlag}
                            onChange={(e) =>
                                formik.setFieldValue(
                                    'errorFlag',
                                    e.target.checked
                                )
                            }
                        />
                        <label
                            htmlFor="errorFlag"
                            className="form-label"
                            style={{ margin: 0 }}>
                            Mark as Error
                        </label>
                    </div>

                    <SubmitButton label={objectToUpdate ? 'Save' : 'Create'} />
                </div>
            </form>
            <br />
            <br />
            {loading && <LoadingSpinner />}
            {error && <ErrorMessage message={error.message} />}
            {manualLicense && (
                <SuccessMessage
                    message={
                        objectToUpdate
                            ? 'License updated successfully!'
                            : 'Manual license created successfully!'
                    }
                />
            )}
        </>
    );
};

CreateUpdateManualTsPlusLicenseForm.propTypes = {
    objectToUpdate: PropTypes.object
};

export default CreateUpdateManualTsPlusLicenseForm;
